<template>
	<div class="container py-4">
		<div class="d-flex align-items-center mb-4">
			<RouterLink
				class="btn btn-outline-secondary me-3"
				to="/_/configuration/integration"
				title="Back to integrations"
			>
				<i class="bi bi-arrow-left"></i>
			</RouterLink>
			<div class="flex-grow-1">
				<h1 class="mb-0">ArcGIS Integration</h1>
				<p class="text-muted mb-0">
					Connect your Esri ArcGIS account and manage the webhook that keeps
					Nidus in sync with ArcGIS data
				</p>
			</div>
			<button
				class="btn btn-outline-secondary"
				title="Refresh status"
				:disabled="loading"
				@click="loadStatus"
			>
				<i class="bi bi-arrow-clockwise" :class="{ 'fa-spin': loading }"></i>
			</button>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>
		<div v-if="notice" class="alert alert-success" role="alert">
			{{ notice }}
		</div>

		<div class="card mb-3">
			<div class="card-body" v-if="status">
				<div class="d-flex align-items-center gap-2 mb-2">
					<span v-if="status.connected" class="badge text-bg-success">
						<i class="bi bi-check-circle me-1"></i>Connected
					</span>
					<span v-else class="badge text-bg-secondary">Not connected</span>
					<span
						v-if="status.webhook && status.webhook.registered"
						class="badge text-bg-success"
					>
						<i class="bi bi-link-45deg me-1"></i>Webhook registered
					</span>
					<span v-else-if="status.connected" class="badge text-bg-warning">
						<i class="bi bi-exclamation-triangle me-1"></i>No webhook yet
					</span>
				</div>

				<div class="row g-3">
					<div class="col-md-6">
						<label class="form-label fw-semibold">Authorized By</label>
						<div>{{ status.authorizedBy || "—" }}</div>
					</div>
					<div class="col-md-6">
						<label class="form-label fw-semibold">Token Age</label>
						<div>
							<span v-if="status.tokenCreated">
								<TimeRelativeWithTooltip :time="status.tokenCreated" />
							</span>
							<span v-else>—</span>
						</div>
					</div>
					<div class="col-md-6">
						<label class="form-label fw-semibold">Environment</label>
						<div>{{ status.environment || "—" }}</div>
					</div>
				</div>
			</div>
			<div v-else-if="loading" class="card-body text-center py-3">
				<div class="spinner-border text-primary" role="status">
					<span class="visually-hidden">Loading…</span>
				</div>
			</div>
		</div>

		<div class="card mb-3">
			<div class="card-body">
				<h5 class="h6 fw-semibold">Webhook</h5>
				<p v-if="!status?.connected" class="text-muted mb-0">
					Connect your ArcGIS account to check whether it can establish the
					webhook that pushes ArcGIS edits to Nidus.
				</p>
				<template v-else>
					<div v-if="status.webhook && status.webhook.registered">
						<dl class="row small mb-1">
							<dt class="col-sm-3">Name</dt>
							<dd class="col-sm-9 mb-0">{{ status.webhook.name }}</dd>
							<dt class="col-sm-3">Callback URL</dt>
							<dd class="col-sm-9 mb-0 text-break">{{ status.webhook.url }}</dd>
							<dt class="col-sm-3">Environment</dt>
							<dd class="col-sm-9 mb-0">{{ status.webhook.environment }}</dd>
							<dt class="col-sm-3">Registered</dt>
							<dd class="col-sm-9 mb-0">
								<span v-if="status.webhook.createdAt">
									<TimeRelativeWithTooltip :time="status.webhook.createdAt" />
								</span>
								<span v-else>—</span>
							</dd>
						</dl>
					</div>
					<p v-else class="text-muted mb-0">
						No webhook is registered for this deployment yet. Run the setup
						below to check your account's webhook rights and register it.
					</p>
					<div
						v-if="status.webhookError"
						class="alert alert-warning small py-2 mt-2 mb-0"
					>
						<div class="fw-semibold">
							<i class="bi bi-exclamation-triangle me-1"></i>Webhook setup could
							not be established
						</div>
						<div class="mt-1">
							This ArcGIS account lacks the privileges to create webhooks, so
							Nidus is using scheduled polling instead. Reconnect with an
							account that can manage webhooks to enable push.
						</div>
						<code class="d-block small mt-1 text-break">
							{{ status.webhookError }}
						</code>
					</div>
				</template>
				<div class="mt-3 d-flex gap-2">
					<a href="/oauth/arcgis/begin" class="btn btn-primary">
						<i class="bi bi-plug me-1"></i>
						{{ status?.connected ? "Reconnect ArcGIS" : "Connect ArcGIS" }}
					</a>
					<button
						type="button"
						class="btn btn-outline-primary"
						:disabled="settingUp"
						@click="runSetup"
					>
						<i class="bi bi-arrow-repeat me-1"></i>Run webhook setup
					</button>
				</div>
			</div>
		</div>

		<div class="card mb-3">
			<div class="card-body">
				<h5 class="h6 fw-semibold">Incoming webhook requests</h5>
				<div class="row text-center">
					<div class="col">
						<div class="fs-3">{{ status?.incoming?.total ?? 0 }}</div>
						<div class="text-muted small">all time</div>
					</div>
					<div class="col">
						<div class="fs-3">{{ status?.incoming?.last24h ?? 0 }}</div>
						<div class="text-muted small">last 24 hours</div>
					</div>
				</div>
				<div
					v-if="
						status?.incoming?.byMethod &&
						Object.keys(status.incoming.byMethod).length
					"
					class="mt-2"
				>
					<span
						v-for="(count, method) in status.incoming.byMethod"
						:key="method"
						class="badge text-bg-light border me-2"
					>
						{{ method }}: {{ count }}
					</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import { apiClient } from "@/client";

interface ArcgisWebhookStatus {
	registered: boolean;
	name?: string;
	environment?: string;
	hookId?: string;
	url?: string;
	createdAt?: string;
}

interface ArcgisIncomingStats {
	total: number;
	last24h: number;
	byMethod?: Record<string, number>;
}

interface ArcgisIntegrationStatus {
	connected: boolean;
	authorizedBy?: string;
	tokenCreated?: string;
	webhook?: ArcgisWebhookStatus;
	webhookError?: string;
	webhookErrorAt?: string;
	incoming: ArcgisIncomingStats;
	environment: string;
}

const status = ref<ArcgisIntegrationStatus | null>(null);
const loading = ref(true);
const settingUp = ref(false);
const error = ref("");
const notice = ref("");

async function loadStatus() {
	loading.value = true;
	error.value = "";
	try {
		status.value = await apiClient.JSONGet<ArcgisIntegrationStatus>(
			"/api/integration/arcgis",
		);
	} catch (e: any) {
		error.value = e?.message || "Failed to load ArcGIS integration status.";
	} finally {
		loading.value = false;
	}
}

async function runSetup() {
	settingUp.value = true;
	error.value = "";
	notice.value = "";
	try {
		await apiClient.JSONPost("/api/integration/arcgis/webhook/setup", {});
		notice.value =
			"Webhook setup job queued. Refresh shortly to see the result.";
	} catch (e: any) {
		error.value = e?.message || "Failed to queue webhook setup.";
	} finally {
		settingUp.value = false;
	}
}

onMounted(loadStatus);
</script>
